import clipMIcon from "@/assets/images/clip_m.png";
import { CustomMarkdown } from "@/components/CustomMarkdown";
import { useUserStore } from "@/stores/user";
import { StatusSelect } from "./StatusSelect";
import { Ticket, TicketComment } from "./types";
import { useCancelTimer } from "@/hooks/useCancelTimer";
import { cancelScheduledMail } from "@/api/customerTicket";
import { useLoadingStore } from "@/stores/loading";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface Props {
  ticket?: Ticket;
  ticketComments?: TicketComment[];
  isDetail?: boolean;
  getTicket?: () => void;
}

export default function MessageList({
  ticket,
  ticketComments,
  isDetail = false,
  getTicket,
}: Props) {
  const comments = ticket ? ticket.data_ticket_comments : ticketComments ?? [];
  return (
    <div className="customer-ticket-messages">
      <table className="title-tbl">
        <tbody>
          <tr>
            <td className="label">問合せタイトル</td>
            <td className="content">{ticket?.inquiry_title}</td>
            {isDetail && ticket?.data_id && getTicket && (
              <>
                <td className="label">ステータス</td>
                <td className="content">
                  <StatusSelect
                    dataId={ticket?.data_id}
                    getTicket={getTicket}
                    key={`frontend-ticket-message-list-status-select-${ticket?.data_id}`}
                  />
                </td>
              </>
            )}
          </tr>
        </tbody>
      </table>
      {comments?.map((item, index) => (
        <Comment
          key={`frontend-ticket-message-list-${index}`}
          item={item}
          isInquiryUser={item.is_inquiry_user}
          ticket={ticket}
          getTicket={getTicket}
        />
      ))}
    </div>
  );
}

interface CommentProps {
  ticket?: Ticket;
  item: TicketComment;
  isInquiryUser: boolean;
  getTicket?: () => void;
}

const Comment = ({ ticket, item, isInquiryUser, getTicket }: CommentProps) => {
  const user = useUserStore((state) => state.user);
  const setLoading = useLoadingStore((state) => state.setLoading);
  const [isCanceled, setIsCanceled] = useState(false);
  const navigate = useNavigate();
  const { customerId } = useParams();

  // 送信取消タイマー
  const { canCancel, remainingTimeText } = useCancelTimer({
    createdAt: item.created_with_seconds,
    onTimeUp: () => {
      // 時間切れ時の処理（特に何もしない）
    },
  });

  // 送信取消機能が必要かどうかを判定（管理者が送信したコメントで、自分が作成したもので、3分以内で、まだキャンセルされていない）
  const isCreatedMe = user?.id == item.created_by;
  const needsCancelFunction =
    !isInquiryUser && isCreatedMe && canCancel && !isCanceled;

  let messageType: string = "received";
  if (!isInquiryUser) {
    const isSameGroup =
      user?.group_id == item.group_id ||
      user?.display_group_name == item.display_created_group_by;
    if (isCreatedMe) {
      messageType = "sent";
    } else if (isSameGroup) {
      messageType = "sent-from-same-group";
    }
  }

  // 送信取消処理
  const handleCancelMail = async () => {
    if (!item.id) return;

    try {
      setLoading(true);
      const result = await cancelScheduledMail({ data_ticket_comment_id: item.id });
      setIsCanceled(true);

      // チケット自体が削除された場合は一覧画面へ遷移
      if (result.data?.response?.ticket_deleted) {
        navigate(`/customer/ticket/${customerId}`);
        return;
      }

      getTicket?.();

      // キャンセル後のイベントを発火して一時保存データの復元を促す
      const cancelEvent = new CustomEvent("ticketCommentCanceled", {
        detail: {
          dataTicketId: ticket?.data_ticket_id,
          userId: user?.id,
        },
      });
      window.dispatchEvent(cancelEvent);
    } catch (error) {
      console.error("送信取消に失敗しました:", error);
    } finally {
      setLoading(false);
    }
  };

  const messageSentInfo = () => {
    if (isInquiryUser) {
      return `${item.created_group_by} ${item.created_user_by}`;
    }
    // 送信先
    const destination = (() => {
      if (!item.ticket_destinations) {
        return `${ticket?.inquiry_company_name ?? ""} ${
          ticket?.inquiry_user_name ?? ""
        }`;
      }
      return item.ticket_destinations?.name ?? " ";
    })();
    // 送信元
    const group_name = item.display_created_group_by || item.created_group_by;
    const sender = `${group_name} ${item.created_user_by}`;

    return `送信先：${destination} 送信元：${sender}`;
  };

  return (
    <div>
      <div className={`${messageType}-message`}>
        <div className={`${messageType}-message-name`}>{messageSentInfo()}</div>
        <div className={`${messageType}-message-box`}>
          <div>
            <CustomMarkdown content={item.comment} />
          </div>
          {item.attached_files.length > 0 && (
            <>
              <hr />
              {item.attached_files.map((file, index) => (
                <div key={index} className="uk-flex uk-flex-middle">
                  <img
                    src={clipMIcon}
                    width={16}
                    height={16}
                    className="uk-margin-small-right"
                  />
                  <a href={file.filepath} target="_blank">
                    {file.original_filename}
                  </a>
                </div>
              ))}
            </>
          )}
        </div>
        <div className={`${messageType}-message-time`}>{item.created}</div>
        {needsCancelFunction && (
          <div className="cancel-mail-section">
            <div className="cancel-mail-section-controls">
              <span className="cancel-mail-section-timer">
                {remainingTimeText}
              </span>
              <button
                onClick={handleCancelMail}
                className="uk-button--s uk-button-red cancel-mail-section-button"
              >
                送信取消
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
